import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    EventEmitter,
    inject,
    input,
    Input,
    Output,
    ViewChild,
} from '@angular/core';
import {
    InventoryBundleFragment,
    ItemFragment,
} from '@control-tower/platform-wms';
import { ChipStatus, SharedUiModule } from '@pf/shared-ui';
import {
    CpwPickingItemScansComponent,
    ItemVerifiedEvent,
} from './cpw-picking-item-scans.component';
import { CpwItemImageComponent } from './cpw-item-image.component';
import { ItemFacade } from '@pf/platform-item-catalog/domain';
import { asNumberSafe } from '@pf/shared-utility';
import { toObservable } from '@angular/core/rxjs-interop';
import { map, of, switchMap } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { findCustomField } from '@pf/shared/util-platform';
import { ProfileProvider } from '@pf/shared-common';
import { UserAccountType } from '@pf/shared-services';

export type { ItemVerifiedEvent };

export type AdditionalChip = {
    label: string;
    hiddenLabel?: string;
    status: ChipStatus;
    copyText?: string;
    revealed?: boolean;
};

@Component({
    selector: 'cpw-item-details',
    imports: [
        SharedUiModule,
        CpwItemImageComponent,
        CpwPickingItemScansComponent,
        AsyncPipe,
    ],
    templateUrl: './cpw-item-details.component.html',
    styleUrl: './cpw-item-details.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CpwItemDetailsComponent {
    @ViewChild(CpwPickingItemScansComponent)
    defaultScansComponent?: CpwPickingItemScansComponent;

    readonly itemFacade = inject(ItemFacade);
    private readonly profileProvider = inject(ProfileProvider);
    private readonly cdr = inject(ChangeDetectorRef);

    isAdmin =
        this.profileProvider.profile.userGroupType?.toLowerCase() ===
        UserAccountType.Administrator;

    active = input(false);
    completed = input(false);
    chipStatus = input<ChipStatus>();
    additionalChips = input<AdditionalChip[] | null>(null);
    item = input.required<ItemFragment>();
    showBundleQuantities = input(true);
    showScans = input(true);
    /** Show the item description row (the thumbnail always shows). */
    showDescription = input(true);
    isLTL = input(false);
    bundle = input<InventoryBundleFragment | null>();

    @Input() quantity?: number;
    @Input() verifiedLocationScanValue?: string;
    @Input() verifiedItemScanValue?: string;
    @Output() itemVerified = new EventEmitter<ItemVerifiedEvent>();

    itemChipStatus = computed<ChipStatus | undefined>(() => {
        const status = this.chipStatus();
        return status === 'primary' ? 'primary-light' : status;
    });

    bundleQuantities = computed(() => {
        return {
            available: asNumberSafe(this.bundle()?.availableQuantity),
            reserved: asNumberSafe(this.bundle()?.reservedQuantity),
        };
    });

    itemUom$ = toObservable(this.item).pipe(
        switchMap(item =>
            item ? this.itemFacade.getById$(item.id as string) : of(null)
        ),
        map(item => findCustomField(item?.customFields, 'UoM')?.value || 'EA')
    );

    focus() {
        this.defaultScansComponent?.focus();
    }

    onDefaultItemVerified(event: ItemVerifiedEvent) {
        this.itemVerified.emit(event);
    }

    copyItemCode() {
        if (!this.isAdmin) return;
        const code = this.item()?.code;
        if (code) {
            navigator.clipboard.writeText(code);
        }
    }

    onChipClick(chip: AdditionalChip) {
        if (chip.hiddenLabel && !chip.revealed) {
            chip.revealed = true;
            setTimeout(() => {
                chip.revealed = false;
                this.cdr.markForCheck();
            }, 2000);
        }
        if (chip.copyText) {
            navigator.clipboard.writeText(chip.copyText);
        }
    }
}
